import { isPatch, Patch } from '.';
import { isPlainJSObject } from './isPlainJSObject';

export function applyPatch<T>(x: T, patch: Patch): T {
   if (!isPatch) throw new Error(`"${JSON.stringify(patch)}" is not a valid patch`);

   const removedIndexesPerArray: Map<string | null, number[]> = new Map();

   let result = x;
   for (let op of patch.operations) {
      if (op.type === 'set') {
         const set = op;
         result = updateByPath(result, set.path, (x, key) => {
            if (!x) return x;
            x[key] = set.value;
            return x;
         });
      }
      if (op.type === 'remove') {
         const remove = op;
         result = updateByPath(result, remove.path, (target, key, pathToTarget) => {
            if (Array.isArray(target)) {
               const targetIndex = Number.parseInt(key);
               const removedIndexes = removedIndexesPerArray.get(pathToTarget) ?? [];
               const offset = removedIndexes.filter(index => index < targetIndex).length;
               target.splice(targetIndex - offset, 1);
               removedIndexes.push(targetIndex);
               removedIndexesPerArray.set(pathToTarget, removedIndexes);
            } else {
               delete target[key];
            }
            return target;
         });
      }
   }
   return result;
}

function updateByPath<T>(
   x: T,
   path: string,
   update: (target: any, lastSegment: string, pathToTarget: string | null) => any
): any {
   const segments = path.split('/').filter(segment => segment.length !== 0);

   const lastSegment = segments.pop();

   if (!lastSegment) return copy(x);

   const nextToLastSegment = segments.pop();

   if (!nextToLastSegment) return update(copy(x), lastSegment, null);

   const result = copy(x);

   let target = result;

   for (let segment of segments) {
      if (!target[segment]) return result;
      target[segment] = copy(target[segment]);
      target = target[segment];
   }

   target[nextToLastSegment] = update(
      copy(target[nextToLastSegment]),
      lastSegment,
      segments.join('/')
   );

   return result;
}

function copy(x: any): any {
   if (!x) return x;
   if (Array.isArray(x)) return copyArray(x);
   if (isPlainJSObject(x)) return { ...x };
   throw new Error(`Cannot copy non primitive value: "${JSON.stringify(x)}"`);
}

function copyArray(x: any[]) {
   const result = [...x];
   if ('total' in x) (result as any).total = (x as any).total;
   return result;
}
